// Shell de la app: header con logo + usuario, y navegación lateral por rol.
// Responsivo: sidebar fija en escritorio; drawer con hamburguesa en móvil.
import { useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { NotificationBell } from '@/components/NotificationBell'
import { AppVersion } from '@/components/AppVersion'
import type { Role } from '@/types'

interface NavItem {
  to: string
  label: string
  icon: string
  roles: Role[]
}

const NAV: NavItem[] = [
  { to: '/entregas', label: 'Entregas', icon: '📦', roles: ['admin', 'almacen', 'repartidor'] },
  { to: '/salidas', label: 'Qué sale mañana', icon: '📅', roles: ['admin', 'almacen'] },
  { to: '/mapa', label: 'Mapa en vivo', icon: '🗺️', roles: ['admin', 'almacen'] },
  { to: '/cotizaciones', label: 'Cotizaciones', icon: '🧾', roles: ['admin', 'vendedor'] },
  { to: '/usuarios', label: 'Usuarios', icon: '👥', roles: ['admin'] },
]

const ROLE_LABEL: Record<Role, string> = {
  admin: 'Administrador',
  almacen: 'Almacén',
  repartidor: 'Repartidor',
  vendedor: 'Vendedor',
}

function initials(name?: string): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  return (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')
}

export function AppLayout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)

  const links = NAV.filter((n) => (user ? n.roles.includes(user.role) : false))

  const NavLinks = () => (
    <nav className="flex flex-col gap-0.5 p-3">
      {links.map((n) => {
        const active = pathname === n.to || pathname.startsWith(n.to + '/')
        return (
          <Link
            key={n.to}
            to={n.to}
            onClick={() => setOpen(false)}
            className={`group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
              active
                ? 'bg-ds-50 text-ds-700'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            <span
              className={`grid h-7 w-7 place-items-center rounded-lg text-base transition ${
                active ? 'bg-ds-500 text-white' : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
              }`}
            >
              {n.icon}
            </span>
            {n.label}
          </Link>
        )
      })}
    </nav>
  )

  return (
    <div className="flex min-h-full flex-col">
      {/* Header */}
      {/* pt-[env(...)]: colchón bajo la barra de estado cuando la app corre edge-to-edge (Android 15). */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-gray-200 bg-white/90 px-4 py-2.5 pt-[max(0.625rem,env(safe-area-inset-top))] backdrop-blur">
        <div className="flex items-center gap-2">
          <button
            className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menú"
          >
            ☰
          </button>
          <img src="/logo-hd.png" alt="Hotel Depot" className="h-8 w-auto" />
          <span className="hidden text-sm font-semibold text-gray-300 sm:inline">·</span>
          <span className="hidden text-sm font-semibold text-gray-500 sm:inline">Hotel Depot Reparto</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <NotificationBell />
          <div className="hidden text-right sm:block">
            <div className="text-sm font-semibold leading-tight text-gray-800">{user?.name}</div>
            <div className="text-xs text-gray-500">{user ? ROLE_LABEL[user.role] : ''}</div>
          </div>
          <div className="grid h-9 w-9 place-items-center rounded-full bg-ds-500 text-sm font-bold uppercase text-white">
            {initials(user?.name)}
          </div>
          <button
            onClick={() => {
              // Confirmación: "Salir" cierra la SESIÓN, no regresa de un módulo.
              if (window.confirm('¿Cerrar tu sesión y salir de Hotel Depot Reparto?\n\nSi solo quieres regresar a otra pantalla, usa el menú ☰.'))
                logout()
            }}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 ring-1 ring-inset ring-gray-200 hover:bg-gray-100"
          >
            Cerrar sesión
          </button>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar escritorio */}
        <aside className="hidden w-60 shrink-0 border-r border-gray-200 bg-white md:flex md:flex-col">
          <NavLinks />
          <AppVersion className="mt-auto px-6 pb-4" />
        </aside>

        {/* Drawer móvil */}
        {open && (
          <div className="fixed inset-0 z-30 md:hidden" onClick={() => setOpen(false)}>
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
            <aside
              className="absolute left-0 top-0 h-full w-64 animate-fade-in bg-white pt-[env(safe-area-inset-top)] shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 border-b border-gray-200 p-3">
                <img src="/logo-hd.png" alt="Hotel Depot" className="h-7 w-auto" />
                <span className="text-sm font-semibold text-gray-500">Hotel Depot Reparto</span>
              </div>
              <NavLinks />
              <AppVersion className="px-6 pt-2" />
            </aside>
          </div>
        )}

        {/* Contenido */}
        <main className="min-w-0 flex-1 p-4 sm:p-6">{children}</main>
      </div>
    </div>
  )
}
